import _ from "lodash";
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import { RecipientTypeEnum } from "../../common/enums/RecipientTypeEnum";



export type NotificationListEditorStateType = {
    recipientFilterText: string;
    viewSelected: boolean;
    viewOptionState: ViewOptionStateEnum;
    recipientType: RecipientTypeEnum;
    organizationID: number | undefined;
    defaultOrganizationID: number | undefined;
};

enum NotificationListEditorActionType {
    ActiveTabChanged = "ACTIVE_TAB_CHANGED",
    RecipientFilterChanged = "RECIPIENT_FILTER_CHANGED",
    NotificationListFilterChanged = "NOTIFICATIONLIST_FILTER_CHANGED",
    AlarmSchemeFilterChanged = "ALARMSCHEME_FILTER_CHANGED",
    RecipientTypeChanged = "RECIPIENT_TYPE_CHANGED",
    ViewSelectedChanged = "VIEW_SELECTED_CHANGED",
    NotificationTypeChanged = "NOTIFY_TYPE_CHANGED",
    OrganizationChanged = 'ORGANIZATION_CHANGED',
    ResetChanged = "RESET_CHANGED",
}

type NotificationListEditorAction = {
     type: NotificationListEditorActionType.RecipientFilterChanged;
     payload: string;
} | {
    type: NotificationListEditorActionType.RecipientTypeChanged;
    payload: RecipientTypeEnum;
} | {
    type: NotificationListEditorActionType.ViewSelectedChanged;
    payload: boolean;
} | {
    type: NotificationListEditorActionType.OrganizationChanged;
    payload: number | undefined;
} | {
    type: NotificationListEditorActionType.ResetChanged;
    payload: void;
};

const initialState: NotificationListEditorStateType = {
    recipientFilterText: '',
    viewSelected: false,
    viewOptionState: ViewOptionStateEnum.Default,
    recipientType: RecipientTypeEnum.Invalid,
    organizationID: undefined,
    defaultOrganizationID: undefined
};

const optionsChanged = (newState: NotificationListEditorStateType, 
    defaultState: NotificationListEditorStateType) => 
    newState.recipientFilterText !== defaultState.recipientFilterText ||
    newState.viewSelected !== defaultState.viewSelected ||
    newState.recipientType !== defaultState.recipientType ||
    newState.organizationID !== newState.defaultOrganizationID;

function notificationListEditorReducer(state: NotificationListEditorStateType, action: NotificationListEditorAction): NotificationListEditorStateType {
    let copy = _.cloneDeep(state);    

    switch (action.type) {


        case NotificationListEditorActionType.RecipientFilterChanged:
            copy.recipientFilterText = action.payload;
            break;
    
        case NotificationListEditorActionType.ViewSelectedChanged:
            copy.recipientFilterText = '';
            copy.viewSelected = action.payload;
            break;

        case NotificationListEditorActionType.RecipientTypeChanged:
            copy.recipientType = action.payload;
            break;

        case NotificationListEditorActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            if (copy.defaultOrganizationID === undefined) {
                copy.defaultOrganizationID = action.payload;
            }
            break;
                
        case NotificationListEditorActionType.ResetChanged:
            copy = _.cloneDeep(initialState);
            break;

        default:
            return state;
    }

    copy.viewOptionState = optionsChanged(copy, initialState) ? 
                            ViewOptionStateEnum.Changed : 
                            ViewOptionStateEnum.Default;

    return copy;
}

export type UseNotificationListEditorStateType = {
    state: NotificationListEditorStateType;
    setRecipientFilterText: (value: string) => void;
    setRecipientType: (value: RecipientTypeEnum) => void;
    setViewSelected: (value: boolean) => void;
    setOrganization: (value: number | undefined) => void;
    resetState: () => void;
};

export function useNotificationListEditorState(): UseNotificationListEditorStateType {

    const [state, dispatch] = useReducer(notificationListEditorReducer, initialState);


    const setRecipientFilterText = useCallback((value: string) => dispatch({
        type: NotificationListEditorActionType.RecipientFilterChanged,
        payload: value
    }), []);


    const setViewSelected = useCallback((value: boolean) => dispatch({
        type: NotificationListEditorActionType.ViewSelectedChanged,
        payload: value
    }), []);


    const setRecipientType = useCallback((value: RecipientTypeEnum) => dispatch({
        type: NotificationListEditorActionType.RecipientTypeChanged,
        payload: value
    }), []);

    const setOrganization = useCallback((value: number | undefined) => dispatch({
        type: NotificationListEditorActionType.OrganizationChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: NotificationListEditorActionType.ResetChanged,
        payload: undefined
    }), [])

    return {
        state,
        setRecipientFilterText,
        setRecipientType,
        setViewSelected,
        setOrganization,
        resetState
    };
}
